import {
  Box,
  Button,
  Switch,
  Text,
  Flex,
  Image,
  VStack,
  HStack,
  useToast,
  Divider
} from '@chakra-ui/react';
import { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { clawMachinePrizeSchema, ClawMachinePrizeFormData } from '@olagg/validation-schemas';
import { AdminClawMachinePrize } from '@olagg/db-types';
import OlaGGFormInput from '../../components/Forms/OlaggFormInput';
import OlaggForm from '../../components/Forms/OlaggForm';
import OlaggFormSelect from '../../components/Forms/OlaggFormSelect';
import usePrizes from './hooks/usePrizes';

type IProps = {
  initialData?: AdminClawMachinePrize | undefined;
  onSubmit: (data: ClawMachinePrizeFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

type MultilingualTitle = {
  es: string;
  en?: string;
  pt?: string;
};

type MultilingualDescription = {
  es?: string;
  en?: string;
  pt?: string;
};

type MultilingualImage = {
  es?: string;
  en?: string;
  pt?: string;
};

const buildTitle = (prize?: AdminClawMachinePrize): MultilingualTitle => ({
  es: prize?.title || '',
  en: prize?.extraData?.en?.title || '',
  pt: prize?.extraData?.pt?.title || ''
});

const buildDescription = (prize?: AdminClawMachinePrize): MultilingualDescription => ({
  es: prize?.description || '',
  en: prize?.extraData?.en?.description || '',
  pt: prize?.extraData?.pt?.description || ''
});

const buildImage = (prize?: AdminClawMachinePrize): MultilingualImage => ({
  es: prize?.imageUrl || '',
  en: prize?.extraData?.en?.imageUrl || '',
  pt: prize?.extraData?.pt?.imageUrl || ''
});

const ClawMachinePrizeForm: FC<IProps> = ({ initialData, onSubmit, onCancel, loading = false, isEdit = false }) => {
  const { getTokens, getBlockchainTokens, getProducts, getBlockchainNetworks } = usePrizes();
  const [title, setTitle] = useState<MultilingualTitle>(buildTitle(initialData));
  const [description, setDescription] = useState<MultilingualDescription>(buildDescription(initialData));
  const [imageUrl, setImageUrl] = useState<MultilingualImage>(buildImage(initialData));
  const [otherLangs, setOtherLangs] = useState<boolean>(
    !!(initialData?.extraData?.en || initialData?.extraData?.pt)
  );

  const [hasTranslations, setHasTranslations] = useState<boolean>(
    !!(initialData?.title?.en || initialData?.title?.pt ||
      (initialData as any)?.extraData?.en || (initialData as any)?.extraData?.pt)
  );

  const checkForTranslations = () => {
    const hasEnglish = !!(title.en || description.en || imageUrl.en);
    const hasPortuguese = !!(title.pt || description.pt || imageUrl.pt);
    const hasExistingTranslations = !!(initialData?.extraData?.en || initialData?.extraData?.pt);

    return hasEnglish || hasPortuguese || hasExistingTranslations;
  };

  // Estado genérico para todas las opciones
  const [optionsState, setOptionsState] = useState<Record<string, { value: string, label: string }[]>>({});
  const [loadingTokensByNetwork, setLoadingTokensByNetwork] = useState(false);
  const [blockchainNetworkFilter, setBlockchainNetworkFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    setHasTranslations(checkForTranslations());
  }, [title, description, imageUrl]);

  const formatOptionsResponse = useCallback((response: any[], useTokenId: boolean = false, useProductId: boolean = false): { value: string, label: string }[] => {
    if (!Array.isArray(response)) return [];
    return response.map((item: any) => {
      const baseLabel = item.title || item.name || 'Untitled';
      const label = item.faceValue ? `${baseLabel} (${item.faceValue})` : baseLabel;

      let value: string;
      if (useProductId) {
        value = String(item.productId);
      } else if (useTokenId) {
        value = String(item.tokenId);
      } else {
        value = String(item.id);
      }

      return {
        value,
        label
      };
    });
  }, []);

  const loadTokenOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined
      };
      const response = await getTokens(params);
      const formatted = formatOptionsResponse(response)
      return formatted;
    } catch (error) {
      console.error('Error loading token options:', error);
      return [];
    }
  }, [getTokens, formatOptionsResponse]);

  const loadBlockchainTokenOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined
      };
      const response = await getBlockchainTokens(params);
      const formatted = formatOptionsResponse(response)
      return formatted;
    } catch (error) {
      console.error('Error loading blockchain token options:', error);
      return [];
    }
  }, [getBlockchainTokens, formatOptionsResponse]);

  const loadStoreProductOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined
      };
      const response = await getProducts(params);
      const formatted = formatOptionsResponse(response, false, true)
      return formatted;
    } catch (error) {
      console.error('Error loading store product options:', error);
      return [];
    }
  }, [getProducts, formatOptionsResponse]);

  const loadBlockchainNetworkOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined
      };
      const response = await getBlockchainNetworks(params);
      const formatted = formatOptionsResponse(response, false);
      return formatted;
    } catch (error) {
      console.error('Error loading blockchain network options:', error);
      return [];
    }
  }, [getBlockchainNetworks, formatOptionsResponse]);

  const loadBlockchainTokensByNetworkOptions = useCallback(async (inputValue: string, networkName?: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined,
        network: networkName || undefined
      };
      const response = await getBlockchainTokens(params);

      // Filtrar manualmente por el nombre de la red si se proporciona
      const filteredResponse = networkName
        ? response.filter((token: any) => token.network?.name === networkName)
        : response;

      const formatted = formatOptionsResponse(filteredResponse, false);
      return formatted;
    } catch (error) {
      console.error('Error loading blockchain tokens by network options:', error);
      return [];
    }
  }, [getBlockchainTokens, formatOptionsResponse]);

  // Configuración de los selectores (simplificada)
  const getSelectorConfig = useCallback((rewardType: string) => {
    switch (rewardType) {
      case 'token':
        return {
          key: 'token',
          label: 'Token de Premio',
          placeholder: 'Selecciona un token...',
          asyncOptions: loadTokenOptions,
          required: true,
          rewardableType: 'Token'
        };
      case 'blockchain_token':
        return {
          key: blockchainNetworkFilter ? 'blockchain_tokens_by_network' : 'blockchain_token',
          label: 'Token de Blockchain de Premio',
          placeholder: 'Selecciona un token de blockchain...',
          asyncOptions: blockchainNetworkFilter
            ? (inputValue: string) => loadBlockchainTokensByNetworkOptions(inputValue, blockchainNetworkFilter)
            : loadBlockchainTokenOptions,
          required: true,
          rewardableType: 'BlockchainToken'
        };
      case 'store_product':
        return {
          key: 'store_product',
          label: 'Producto de la Tienda ',
          placeholder: 'Selecciona un producto de la tienda...',
          asyncOptions: loadStoreProductOptions,
          required: true,
          rewardableType: 'Product'
        };
      default:
        return null;
    }
  }, [blockchainNetworkFilter, loadTokenOptions, loadBlockchainTokenOptions, loadBlockchainTokensByNetworkOptions, loadStoreProductOptions]);

  // Cargar todas las opciones iniciales al montar el componente - SOLO UNA VEZ
  useEffect(() => {
    const loadAllInitialOptions = async () => {
      try {
        // Cargar tokens
        const tokensResponse = await getTokens({ page: 1, limit: 100 });
        const formattedTokens = formatOptionsResponse(tokensResponse, true, false);

        // Cargar blockchain tokens
        const blockchainTokensResponse = await getBlockchainTokens({ page: 1, limit: 100 });
        const formattedBlockchainTokens = formatOptionsResponse(blockchainTokensResponse, false, false);

        // Cargar productos de la tienda
        const storeProductsResponse = await getProducts({ page: 1, limit: 100 });
        const formattedStoreProducts = formatOptionsResponse(storeProductsResponse, false, true);

        // Cargar blockchain networks
        const networksResponse = await getBlockchainNetworks({ page: 1, limit: 100 });
        const formattedNetworks = networksResponse.map((item: any) => ({
          value: item.name,
          label: item.name
        }));

        setOptionsState({
          token: formattedTokens,
          blockchain_token: formattedBlockchainTokens,
          store_product: formattedStoreProducts,
          blockchain_networks: formattedNetworks,
          blockchain_tokens_by_network: []
        });
      } catch (error) {
        console.error('Error loading initial options:', error);
      }
    };

    loadAllInitialOptions();
  }, []);

  const rewardTypeOptions = [
    { value: 'xp', label: 'XP' },
    { value: 'treasure_chance', label: 'Chance Extra Juego del Tesoro' },
    { value: 'claw_coin', label: 'Moneda del Juego de la Garra' },
    { value: 'token', label: 'Insignia' },
    { value: 'blockchain_token', label: 'Token de Blockchain' },
    { value: 'store_product', label: 'Cupón de Producto de la Tienda' },
    { value: 'store_discount', label: 'Cupón de Descuento de la Tienda' },
    { value: 'physical_item', label: 'Producto Físico' },
  ];

  const initialRewardableId = () => {
    if (initialData?.rewardType === "store_product") {
      return initialData?.rewardableInfo?.uid;
    }

    return initialData?.rewardableId ? String(initialData.rewardableId) : undefined;
  }

  const useFormMethods = useForm<ClawMachinePrizeFormData>({
    resolver: yupResolver(clawMachinePrizeSchema),
    mode: 'onChange',
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      imageUrl: initialData?.imageUrl || '',
      rewardType: initialData?.rewardType || undefined,
      rewardableType: initialData?.rewardableType || undefined,
      rewardableId: initialData?.rewardableId ? String(initialData.rewardableId) : undefined,
      extra_data: {
        ...initialData?.extraData,
        limit: (initialData?.extraData as any)?.limit
      }
    }
  });

  // Agregar la opción actual del initialData si existe y no está en las opciones
  useEffect(() => {
    if (isEdit && initialData?.rewardableInfo && initialData?.rewardableType && initialData?.rewardableId) {
      const currentOption = {
        value: String(initialData.rewardableId),
        label: initialData.rewardableInfo.title || 'Untitled'
      };

      // Determinar qué tipo de selector necesita la opción actual
      let targetKey: string | null = null;

      if (initialData.rewardType === 'token') {
        targetKey = 'token';
      } else if (initialData.rewardType === 'blockchain_token') {
        targetKey = 'blockchain_token';
      } else if (initialData.rewardType === 'store_product') {
        targetKey = 'store_product';
      }

      if (targetKey && currentOption.value) {
        setOptionsState(prev => {
          const currentOptions = prev[targetKey!] || [];
          const exists = currentOptions.some(option => option.value === currentOption.value);
          if (!exists) {
            return {
              ...prev,
              [targetKey!]: [currentOption, ...currentOptions]
            };
          }
          return prev;
        });
      }
    }
  }, [isEdit, initialData?.rewardableId, initialData?.rewardableType]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    getValues
  } = useFormMethods;

  const watchedImage = watch('imageUrl');
  const watchedRewardType = watch('rewardType');
  const watchedBlockchainNetworkId = watch('extra_data.blockchainTokenNetworkId');
  const watchedRewardableId = watch('rewardableId');

  useEffect(() => {
    console.log('Current rewardType:', watchedRewardType);
  }, [watchedRewardType]);

  // Cargar tokens filtrados por network cuando cambie el network filter para blockchain_token
  useEffect(() => {
    if (watchedRewardType === 'blockchain_token' && blockchainNetworkFilter && !loadingTokensByNetwork) {
      setLoadingTokensByNetwork(true);

      const loadTokensByNetwork = async () => {
        try {
          const tokensResponse = await getBlockchainTokens({
            page: 1,
            limit: 100,
            network: blockchainNetworkFilter
          });

          // Filtrar manualmente por el nombre de la red
          const filteredTokens = tokensResponse.filter((token: any) =>
            token.network?.name === blockchainNetworkFilter
          );

          const formattedTokens = formatOptionsResponse(filteredTokens, false);

          setOptionsState(prev => ({
            ...prev,
            blockchain_tokens_by_network: formattedTokens
          }));

          // Resetear el token seleccionado cuando cambia el network
          setValue('rewardableId', undefined);
        } catch (error) {
          console.error('Error loading blockchain tokens by network:', error);
        } finally {
          setLoadingTokensByNetwork(false);
        }
      };

      loadTokensByNetwork();
    }
  }, [blockchainNetworkFilter, watchedRewardType]);

  // Resetear campos cuando cambia el rewardType
  useEffect(() => {
    // Solo resetear si no estamos en modo edición inicial
    if (!isEdit || (isEdit && watchedRewardType !== initialData?.rewardType)) {
      // Resetear rewardableId usando setValue
      setValue('rewardableId', undefined);

      // Resetear rewardableType también
      setValue('rewardableType', undefined);

      // Resetear filtro de network
      setBlockchainNetworkFilter(undefined);

      // Si cambia a store_discount, resetear los campos específicos
      if (watchedRewardType === 'store_discount') {
        setValue('extra_data.limit', undefined);
      }
    }
  }, [watchedRewardType, setValue, isEdit, initialData?.rewardType]);

  // Limpiar filtro de network cuando se selecciona un rewardableId
  useEffect(() => {
    if (watchedRewardableId && blockchainNetworkFilter) {
      // Si se selecciona un token, ya no necesitamos el filtro
      setBlockchainNetworkFilter(undefined);
    }
  }, [watchedRewardableId]);

  // Cargar opciones específicas para store_discount en modo edición
  useEffect(() => {
    if (isEdit && initialData?.rewardType === 'store_discount' && initialData?.extraData) {
      const loadStoreDiscountEditOptions = async () => {
        try {
          const extraData = initialData.extraData as any;

          // Si hay un network_id, cargar los tokens para ese network
          if (extraData?.blockchainTokenNetworkId) {
            const tokensResponse = await getBlockchainTokens({
              page: 1,
              limit: 100,
              network: extraData.blockchainTokenNetworkId
            });

            // Filtrar manualmente por el nombre de la red
            const filteredTokens = tokensResponse.filter((token: any) =>
              token.network?.name === extraData.blockchainTokenNetworkId
            );

            const formattedTokens = formatOptionsResponse(filteredTokens, false);

            setOptionsState(prev => ({
              ...prev,
              blockchain_tokens_by_network: formattedTokens
            }));
          }
        } catch (error) {
          console.error('Error loading store discount edit options:', error);
        }
      };

      loadStoreDiscountEditOptions();
    }
  }, [isEdit, initialData?.rewardType]);

  // Cargar tokens filtrados por network cuando cambie el network seleccionado
  useEffect(() => {
    if (watchedRewardType === 'store_discount' && watchedBlockchainNetworkId && !loadingTokensByNetwork) {
      setLoadingTokensByNetwork(true);

      const loadTokensByNetwork = async () => {
        try {
          const tokensResponse = await getBlockchainTokens({
            page: 1,
            limit: 100,
            network: watchedBlockchainNetworkId
          });

          // Filtrar manualmente por el nombre de la red
          const filteredTokens = tokensResponse.filter((token: any) =>
            token.network?.name === watchedBlockchainNetworkId
          );

          const formattedTokens = formatOptionsResponse(filteredTokens, false);

          setOptionsState(prev => ({
            ...prev,
            blockchain_tokens_by_network: formattedTokens
          }));

          // Resetear el token seleccionado cuando cambia el network
          setValue('extra_data.blockchainTokenId', undefined);
        } catch (error) {
          console.error('Error loading blockchain tokens by network:', error);
        } finally {
          setLoadingTokensByNetwork(false);
        }
      };

      loadTokensByNetwork();
    }
  }, [watchedBlockchainNetworkId, watchedRewardType]);

  const beforeSubmit = async (data: ClawMachinePrizeFormData) => {
    const config = getSelectorConfig(data.rewardType);

    // Preparar datos finales con lógica clara
    const baseData = {
      ...data,
      title: title.es,
      description: description.es,
      imageUrl: imageUrl.es,
      rewardType: data.rewardType,
      rewardableId: data.rewardableId,
      extra_data: {
        ...data.extra_data,
        ...(hasTranslations && {
          en: {
            title: title.en || '',
            description: description.en || '',
            imageUrl: imageUrl.en || ''
          },
          pt: {
            title: title.pt || '',
            description: description.pt || '',
            imageUrl: imageUrl.pt || ''
          },
          translated: true
        })
      }
    };

    // Lógica para store_discount
    if (data.rewardType === 'store_discount') {
      // Para store_discount, los datos van en extra_data y no necesitamos rewardableType/rewardableId
      baseData.rewardableType = undefined;
      baseData.rewardableId = undefined;

      // Asegurar que los campos específicos del store_discount estén en extra_data
      baseData.extra_data = {
        ...baseData.extra_data,
        limit: data.extra_data?.limit
      };
    } else if (config && data.rewardableId) {
      // Para otros tipos de premio, usar la lógica existente
      baseData.rewardableType = config.rewardableType;
      baseData.rewardableId = data.rewardableId;
    } else {
      // Explícitamente remover estos campos si no aplican
      baseData.rewardableType = undefined;
      baseData.rewardableId = undefined;
    }

    await onSubmit(baseData as ClawMachinePrizeFormData);
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{
        width: '100%'
      }}
    >
      <VStack color={'white'} w={'full'} maxW='600px' margin='0 auto'>

        {/* Switch para otros idiomas */}
        <Flex
          w="full"
          justify="start"
          align="center"
          style={{ marginBottom: '20px' }}
        >
          <Text color="white" style={{ cursor: 'pointer' }}>
            Otros idiomas
          </Text>
          <Switch
            colorScheme="purple"
            isChecked={otherLangs}
            size="md"
            ml={2}
            onChange={() => setOtherLangs(prev => !prev)}
          />
        </Flex>
        {/* Título en Español */}
        <OlaGGFormInput
          label='Título (Español)'
          controlName='title'
          placeholder='Título del premio'
          required={true}
          onChange={(e) => {
            setTitle({ ...title, es: e.target.value });
          }}
        />

        {/* Título en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Título en inglés */}
            <OlaGGFormInput
              label='Título en Inglés'
              controlName='extra_data.en.title'
              placeholder='Prize Title in English'
              onChange={(e) => setTitle({ ...title, en: e.target.value })}
            />

            {/* Título en portugués */}
            <OlaGGFormInput
              label='Título en Portugués'
              controlName='extra_data.pt.title'
              placeholder='Título da Premiação em Português'
              onChange={(e) => setTitle({ ...title, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Descripción */}
        <OlaGGFormInput
          label='Descripción'
          controlName='description'
          placeholder='Descripción del premio'
          inputType='textarea'
          onChange={(e) => setDescription({ ...description, es: e.target.value })}
        />

        {/* Descripción en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Descripción en inglés */}
            <OlaGGFormInput
              label='Descripción en Inglés'
              controlName='extra_data.en.description'
              placeholder='Prize Description in English'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, en: e.target.value })}
            />

            {/* Descripción en portugués */}
            <OlaGGFormInput
              label='Descripción en Portugués'
              controlName='extra_data.pt.description'
              placeholder='Descrição da Premiação em Português'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Tipo de Reward */}
        <OlaggFormSelect
          controlName="rewardType"
          label="Tipo de Premio"
          placeholder="Selecciona el tipo de premio"
          staticOptions={rewardTypeOptions}
          required={true}
          newVersion
        />

        {/* Filtro de Network para blockchain_token - Solo se muestra si no hay rewardableId seleccionado */}
        {watchedRewardType === 'blockchain_token' && !watchedRewardableId && (
          <Box w="full" bgColor='#20173d' p='4' borderRadius={'12px'} mb={2}>
            <Text color="white" fontSize="sm" mb={2} fontWeight={600}>
              Filtrar por Red Blockchain (Opcional)
            </Text>
            <select
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'black',
                color: 'white',
                border: '1px solid #4A5568',
                fontSize: '16px'
              }}
              value={blockchainNetworkFilter || ''}
              onChange={(e) => {
                const value = e.target.value;
                setBlockchainNetworkFilter(value || undefined);
              }}
            >
              <option value="">Todas las redes</option>
              {optionsState['blockchain_networks']?.map((network) => (
                <option key={network.value} value={network.value}>
                  {network.label}
                </option>
              ))}
            </select>
            <Text color="gray.400" fontSize="xs" mt={2}>
              Filtra los tokens de blockchain por red para facilitar la búsqueda
            </Text>
          </Box>
        )}

        {/* Target ID - Selector dinámico según rewardType */}
        {(() => {
          const config = getSelectorConfig(watchedRewardType);

          if (!config) return null;

          return (
            <Box w="full">
              <OlaggFormSelect
                controlName="rewardableId"
                label={config.label}
                placeholder={config.placeholder}
                staticOptions={optionsState[config.key] || []}
                asyncOptions={config.asyncOptions}
                isSearchable={true}
                newVersion
                required={config.required}
              />
            </Box>
          );
        })()}

        {/* Campos específicos para Store Discount */}
        {watchedRewardType === 'store_discount' && (
          <Box w="full" bgColor='#20173d' p='6' borderRadius={'12px'} mb={4}>
            <Text color="white" fontSize="lg" fontWeight="bold" mb={4}>
              Configuración del Cupón de Descuento
            </Text>

            {/* Input de Límite */}
            <Box>
              <OlaGGFormInput
                label='Límite de Descuento'
                controlName='extra_data.limit'
                placeholder='0.00'
                required={true}
                inputType='number'
              />
              <Text color="gray.400" fontSize="sm" mt={1}>
                Monto máximo que se puede descontar del precio
              </Text>
            </Box>
          </Box>
        )}

        {/* URL de Imagen */}
        <OlaGGFormInput
          label='URL de Imagen'
          controlName='imageUrl'
          placeholder='https://ejemplo.com/imagen.jpg'
          onChange={(e) => setImageUrl({ ...imageUrl, es: e.target.value })}
        />

        {/* Imagen en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Imagen en inglés */}
            <OlaGGFormInput
              label='URL de Imagen en Inglés'
              controlName='extra_data.en.imageUrl'
              placeholder='https://example.com/image-en.jpg'
              onChange={(e) => setImageUrl({ ...imageUrl, en: e.target.value })}
            />

            {/* Imagen en portugués */}
            <OlaGGFormInput
              label='URL de Imagen en Portugués'
              controlName='extra_data.pt.imageUrl'
              placeholder='https://exemplo.com/imagem-pt.jpg'
              onChange={(e) => setImageUrl({ ...imageUrl, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Vista previa de imagen */}
        {(imageUrl.es || imageUrl.en || imageUrl.pt) && (
          <Box mb={4}>
            <Text color="white" fontSize="sm" mb={2}>Vista previa de imágenes:</Text>
            <HStack align="start" spacing={4}>
              {imageUrl.es && (
                <Box>
                  <Text color="white" fontSize="xs" mb={1} fontWeight="bold">Español:</Text>
                  <Image
                    src={imageUrl.es}
                    alt="Vista previa español"
                    maxW="300px"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="300px"
                        h="200px"
                        bg="gray.600"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.400">No se pudo cargar la imagen</Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              {imageUrl.en && (
                <Box>
                  <Text color="purple.300" fontSize="xs" mb={1} fontWeight="bold">Inglés (EN):</Text>
                  <Image
                    src={imageUrl.en}
                    alt="Vista previa inglés"
                    maxW="300px"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="300px"
                        h="200px"
                        bg="gray.600"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.400">Could not load image</Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              {imageUrl.pt && (
                <Box>
                  <Text color="green.300" fontSize="xs" mb={1} fontWeight="bold">Portugués (PT):</Text>
                  <Image
                    src={imageUrl.pt}
                    alt="Vista previa portugués"
                    maxW="300px"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="300px"
                        h="200px"
                        bg="gray.600"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.400">Não foi possível carregar a imagem</Text>
                      </Box>
                    }
                  />
                </Box>
              )}
            </HStack>
          </Box>
        )}

        <Button
          variant={'filledGradient'}
          type="submit"
          w={'full'}
          style={{
            marginTop: '30px'
          }}
          isLoading={loading}
          loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
        >
          {isEdit ? 'Actualizar Premio' : 'Crear Premio'}
        </Button>
      </VStack>
    </OlaggForm>
  );
};

export default ClawMachinePrizeForm;
export { ClawMachinePrizeForm as Form };
